import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, { useEffect } from "react";
import { Stack } from "@mui/material";
import {
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
  CornerRadius,
  Snackbar,
  RadioButtonColorTokens,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import { DividerComponent } from "../commonQuestionConfigurationDetailsComponents/DividerComponent";
import { BorderStyle, EMPTY_CHARACTER } from "@pulse/shared-components";
import {
  Dialog,
  LoadingIndicator,
} from "@surya-digital/leo-reactjs-material-ui";
import { ErrorSuccessDialogContent } from "../../../projects/components/ErrorSuccessDialogContent";
import { NavigateToFunctions } from "../../../../routes/RoutesHelper";
import { SurveyStore } from "../../store/SurveyStore";
import {
  CommonProjectAlreadyArchivedRPCError,
  UpdateSurveySettingsError,
} from "../../store/BackOfficeSurveySettingsStore";
import { SurveySettingsPaneTab } from "./SurveySettingsPaneTab";
import { SurveySettingsGeneralTab } from "./tabs/SurveySettingsGeneralTab";
import { SurveySettingsSurveyControlTab } from "./tabs/SurveySettingsSurveyControlTab";
import { SurveySettingsDialogCustomizationTab } from "./tabs/SurveySettingsDialogCustomizationTab";
import { SurveySettingsSurveyUITab } from "./tabs/SurveySettingsSurveyUITab";
import { SurveySettingsAdvancedFeaturesTab } from "./tabs/SurveySettingsAdvancedFeaturesTab";

interface SurveySettingsPaneProps {
  surveyStore: Instance<typeof SurveyStore>;
  spacing: Spacing;
  t: TFunction;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  border: BorderStyle;
  borderRadius: CornerRadius;
  navigateTo: NavigateToFunctions;
  surveySettingsPageRef: React.RefObject<HTMLDivElement>;
  radioButtonTokens: RadioButtonColorTokens<string>;
}

export const SurveySettingsPane = observer(
  ({
    surveyStore,
    spacing,
    t,
    typography,
    tokens,
    surveySettingsPageRef,
  }: SurveySettingsPaneProps): React.ReactElement => {
    const updatedBackOfficeSurveySettings =
      surveyStore.backOfficeSurveySettingsStore.updatedBackOfficeSurveySettings;
    const updatedBackOfficeSurveySettingsUIStore =
      updatedBackOfficeSurveySettings?.backOfficeSurveySettingsUIStore;

    useEffect(() => {
      return () => {
        surveyStore.clearRpcErrors();
        surveyStore.backOfficeSurveySettingsStore.uploadClientLogoStore.resetFileUploadError();
      };
    }, []);

    const getSaveChangesErrorDialogText = (): string => {
      switch (surveyStore.backOfficeSurveySettingsStore.rpcError) {
        case CommonProjectAlreadyArchivedRPCError.ProjectAlreadyArchived:
          return t(
            "surveys.settings.saveChanges.saveChangesProjectAlreadyArchivedErrorText",
          );
        case UpdateSurveySettingsError.SurveyIsClosed:
          return t(
            "surveys.settings.saveChanges.saveChangesSurveyClosedErrorText",
          );
        default:
          console.error(
            `No error text set for error ${surveyStore.backOfficeSurveySettingsStore.rpcError} `,
          );
          return EMPTY_CHARACTER;
      }
    };
    const getSnackbarMessage = (): string => {
      switch (surveyStore.backOfficeSurveySettingsStore.rpcError) {
        case UpdateSurveySettingsError.DeletedRespondentColumn: {
          return t(
            "surveys.settings.surveyHeaderTextSettings.invalidRespondentColumn",
          );
        }
        default:
          return t("surveys.settings.errorBannerText");
      }
    };

    if (
      surveyStore.backOfficeSurveySettingsStore.isGetSurveySettingsRPCLoading
    ) {
      return (
        <LoadingIndicator
          isLoading={
            surveyStore.backOfficeSurveySettingsStore
              .isGetSurveySettingsRPCLoading
          }
        />
      );
    }

    const isLoading =
      surveyStore.backOfficeSurveySettingsStore
        .isUpdateSurveySettingsRPCLoading ||
      surveyStore.backOfficeSurveySettingsStore.isResetClientLogoLoading;

    return (
      <Stack
        direction="row"
        width="100%"
        height="100%"
        px={spacing.space2XL}
        gap={spacing.space2XL}
      >
        <SurveySettingsPaneTab />
        <Stack
          width="100%"
          height="100%"
          divider={<DividerComponent orientation="horizontal" width="100%" />}
          ref={surveySettingsPageRef}
        >
          <SurveySettingsGeneralTab />
          <SurveySettingsSurveyControlTab />
          <SurveySettingsAdvancedFeaturesTab />
          <SurveySettingsDialogCustomizationTab />
          <SurveySettingsSurveyUITab />
        </Stack>
        {isLoading && <LoadingIndicator isLoading />}
        <Snackbar
          message={getSnackbarMessage()}
          severity="error"
          onClose={(): void => {
            updatedBackOfficeSurveySettingsUIStore?.setIsErrorSnackbarVisible(
              false,
            );
          }}
          isOpen={
            !!updatedBackOfficeSurveySettingsUIStore?.isErrorSnackbarVisible
          }
          isDismissible={true}
        />
        {surveyStore.backOfficeSurveySettingsStore
          .isUpdateSurveySettingsErrorDialogVisible && (
          <Dialog
            open={
              surveyStore.backOfficeSurveySettingsStore
                .isUpdateSurveySettingsErrorDialogVisible
            }
            title={t(
              "surveys.settings.saveChanges.saveChangesErrorDialogTitle",
            )}
            secondaryButtonText={t("common.close")}
            onSecondaryButtonClick={() =>
              surveyStore.backOfficeSurveySettingsStore.setIsUpdateSurveySettingsErrorDialogVisible(
                false,
              )
            }
          >
            <ErrorSuccessDialogContent
              spacing={spacing}
              tokens={tokens}
              typography={typography}
              isError={true}
              errorSuccessText={getSaveChangesErrorDialogText()}
            />
          </Dialog>
        )}
      </Stack>
    );
  },
);
